import { useEffect, useState, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

const STALE_THRESHOLD_MS = 15 * 60 * 1000; // 15 minutes
const CLEANUP_INTERVAL_MS = 2 * 60 * 1000; // Run every 2 minutes

export function useStaleEnrichmentCleanup(companyId: string | undefined | null, userId?: string | null) {
  const [cleanupDone, setCleanupDone] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!companyId || !userId) return;

    const runCleanup = async () => {
      const cutoff = new Date(Date.now() - STALE_THRESHOLD_MS).toISOString();

      await Promise.all([
        // Stale enrichment_jobs — now includes 'pending' status
        supabase
          .from('enrichment_jobs')
          .update({ status: 'failed', error_message: 'Auto-cleanup: stale job', updated_at: new Date().toISOString() })
          .in('status', ['pending', 'processing', 'scraping', 'extracting'])
          .lt('updated_at', cutoff),

        // Stale web_search_status — now includes 'retrying'
        supabase
          .from('leads')
          .update({ web_search_status: 'failed', updated_at: new Date().toISOString() })
          .eq('company_id', companyId)
          .eq('created_by', userId)
          .in('web_search_status', ['processing', 'pending', 'retrying'])
          .lt('updated_at', cutoff),

        // Stale deep_research_status — now includes 'retrying'
        supabase
          .from('leads')
          .update({ deep_research_status: 'failed', updated_at: new Date().toISOString() })
          .eq('company_id', companyId)
          .eq('created_by', userId)
          .in('deep_research_status', ['processing', 'scraping', 'extracting', 'retrying'])
          .lt('updated_at', cutoff),
      ]);

      setCleanupDone(true);
    };

    // Run immediately on mount
    runCleanup();

    // Then run on interval
    intervalRef.current = setInterval(runCleanup, CLEANUP_INTERVAL_MS);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [companyId, userId]);

  return cleanupDone;
}
